import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/axios';
import { UserCheck, UserX, Clock, Users, Shield, User } from 'lucide-react';

// Using centralized api

function UserManagement() {
  const { user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        api.get('/users/pending'),
        api.get('/users/all')
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId, userName) => {
    setActionLoading(userId);
    try {
      await api.post(`/users/${userId}/approve`);
      toast.success(`${userName} ha sido aprobado`);
      fetchUsers();
    } catch (error) {
      toast.error('Error al aprobar usuario');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`¿Estás seguro de rechazar y eliminar a ${userName}?`)) return;
    
    setActionLoading(userId);
    try {
      await api.delete(`/users/${userId}/reject`);
      toast.success(`${userName} ha sido rechazado y eliminado`);
      fetchUsers();
    } catch (error) {
      toast.error('Error al rechazar usuario');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'agent') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">No tienes permisos para acceder a esta página</p>
        </div>
      </Layout>
    );
  }

  const approvedUsers = allUsers.filter(u => u.approved);
  const stats = {
    total: allUsers.length,
    pending: pendingUsers.length,
    approved: approvedUsers.length,
    agents: allUsers.filter(u => u.role === 'agent').length,
    players: allUsers.filter(u => u.role === 'player').length
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="user-management-page">
        <div>
          <h1 className="text-2xl font-bold text-[#4F4F4F]">Gestionar Usuarios</h1>
          <p className="text-muted-foreground">Aprueba o rechaza solicitudes de acceso</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Users className="h-8 w-8 text-[#D4AF37]" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.pending > 0 ? "border-orange-300 bg-orange-50" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pendientes</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Aprobados</p>
                  <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                </div>
                <UserCheck className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Agentes</p>
                  <p className="text-2xl font-bold">{stats.agents}</p>
                </div>
                <Shield className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Jugadores</p>
                  <p className="text-2xl font-bold">{stats.players}</p>
                </div>
                <User className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList>
            <TabsTrigger value="pending" className="relative">
              Pendientes
              {stats.pending > 0 && (
                <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
                  {stats.pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="all">Todos los Usuarios</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Cargando...</div>
            ) : pendingUsers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <UserCheck className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <p className="text-muted-foreground">No hay solicitudes pendientes</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map((pendingUser) => (
                  <Card key={pendingUser.user_id} className="border-orange-200" data-testid={`pending-user-${pendingUser.user_id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A67C00] flex items-center justify-center text-white font-bold">
                            {pendingUser.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-semibold">{pendingUser.name}</p>
                            <p className="text-sm text-muted-foreground">{pendingUser.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={pendingUser.role === 'agent' ? 'default' : 'secondary'}>
                                {pendingUser.role === 'agent' ? 'Staff' : 'Jugador'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(pendingUser.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => handleReject(pendingUser.user_id, pendingUser.name)}
                            disabled={actionLoading === pendingUser.user_id}
                            data-testid={`reject-btn-${pendingUser.user_id}`}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Rechazar
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(pendingUser.user_id, pendingUser.name)}
                            disabled={actionLoading === pendingUser.user_id}
                            data-testid={`approve-btn-${pendingUser.user_id}`}
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Aprobar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Cargando...</div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Todos los Usuarios</CardTitle>
                  <CardDescription>Lista completa de usuarios registrados</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {allUsers.map((u) => (
                      <div 
                        key={u.user_id} 
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                        data-testid={`user-row-${u.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A67C00] flex items-center justify-center text-white font-bold text-sm">
                            {u.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={u.role === 'agent' ? 'default' : 'secondary'}>
                            {u.role === 'agent' ? 'Staff' : 'Jugador'}
                          </Badge>
                          {u.approved ? (
                            <Badge variant="outline" className="text-green-600 border-green-300">
                              <UserCheck className="h-3 w-3 mr-1" />
                              Aprobado
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              <Clock className="h-3 w-3 mr-1" />
                              Pendiente
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default UserManagement;
